import { Link } from "react-router-dom";
import { ArrowRight, Glasses } from "lucide-react";
import { Reveal, ChapterTag, MaskedLine } from "@/components/Reveal";
import SystemFlow from "@/components/SystemFlow";

const hardware = [
  "Connected Devices", "Sensors", "IoT Hardware", "GPS Devices", "Cellular Communications",
  "Bluetooth / BLE", "Wi-Fi", "RFID", "Cameras", "Smart Glasses", "Asset Tracking Hardware",
  "Edge Devices", "Device Integration", "Field-Deployed Technology",
];

const software = [
  "Web Applications", "Mobile Applications", "Cloud Platforms", "APIs", "Databases",
  "Dashboards", "Business Applications", "Automation", "AI Applications", "Computer Vision",
  "Generative AI", "AI Agents", "System Integrations",
];

const flows = [
  { nodes: ["Sensor", "Connectivity", "Cloud", "Software", "AI", "Action"], label: "FLOW 01 // SENSE → ACT" },
  { nodes: ["Camera", "Computer Vision", "Dashboard", "Alert"], label: "FLOW 02 // SEE → ALERT" },
  { nodes: ["GPS Device", "Cellular Network", "Cloud Platform", "Tracking Application"], label: "FLOW 03 // TRACK → LOCATE" },
  { nodes: ["Smart Glasses", "Voice / Camera", "AI", "Business System"], label: "FLOW 04 // WEAR → ASSIST" },
  { nodes: ["IoT Device", "Data", "Automation", "Business Workflow"], label: "FLOW 05 // CONNECT → AUTOMATE" },
];

const ChipGrid = ({ title, items, testId, accent }) => (
  <div data-testid={testId} className="border border-white/10 bg-[#0A0A0A] p-8 md:p-10">
    <div className="flex items-center gap-4 mb-8">
      <span className={`w-2 h-2 ${accent ? "bg-cyan" : "bg-white/40"}`} />
      <h3 className="font-unbounded font-normal uppercase tracking-tight text-lg md:text-xl text-white">{title}</h3>
    </div>
    <div className="flex flex-wrap gap-2.5">
      {items.map((item) => (
        <span
          key={item}
          className="border border-white/12 bg-white/[0.02] px-3.5 py-2 font-mono text-[10px] md:text-[11px] tracking-[0.15em] text-neutral-300 uppercase hover:border-cyan/50 hover:text-cyan transition-colors duration-300 cursor-default"
        >
          {item}
        </span>
      ))}
    </div>
  </div>
);

export default function Experience() {
  return (
    <main data-testid="experience-page" className="pt-20">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
        <ChapterTag num="04" label="Experience" />
        <MaskedLine className="mt-12 font-unbounded font-extrabold uppercase tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl">
          Technology
        </MaskedLine>
        <MaskedLine delay={0.12} className="font-unbounded font-extrabold uppercase tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl text-outline">
          Experience
        </MaskedLine>
        <Reveal delay={0.25}>
          <p className="mt-10 max-w-3xl text-base md:text-lg text-neutral-400 leading-relaxed">
            My experience spans the full technology ecosystem — from physical hardware, connected devices, and wireless communications to software applications, cloud platforms, automation, and artificial intelligence.
          </p>
        </Reveal>
      </section>

      <section data-testid="hw-sw-highlight" className="border-t border-white/10 bg-[#070707]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <Reveal>
              <h2 className="font-unbounded font-semibold uppercase tracking-tight leading-[1.08] text-2xl sm:text-4xl lg:text-[2.8rem]">
                Extensive<br />
                <span className="text-cyan">Hardware + Software</span><br />
                Experience
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-6 space-y-6">
            <Reveal delay={0.1}>
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed">
                Many technology professionals specialize in either hardware or software. <span className="text-white">My experience combines both.</span>
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed">
                I understand how physical devices, sensors, communications, embedded technology, mobile applications, cloud services, APIs, databases, dashboards, automation, and AI work together as a complete system.
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <p className="text-sm md:text-base text-neutral-400 leading-relaxed">
                This allows me to approach a business problem from the entire technology stack rather than viewing it through only one technical specialty.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="experience-grids" className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Reveal>
            <ChipGrid title="Hardware Experience" items={hardware} testId="hardware-grid" accent />
          </Reveal>
          <Reveal delay={0.1}>
            <ChipGrid title="Software Experience" items={software} testId="software-grid" />
          </Reveal>
        </div>
      </section>

      <section data-testid="integration-flows" className="border-t border-white/10 bg-[#070707]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <ChapterTag num="05" label="Integration" />
          <Reveal delay={0.1}>
            <h2 className="mt-12 font-unbounded font-semibold uppercase tracking-tight text-2xl sm:text-4xl leading-[1.1] max-w-3xl">
              The real opportunity happens where <span className="text-cyan">hardware and software meet.</span>
            </h2>
          </Reveal>
          <div className="mt-16 space-y-10">
            {flows.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.08}>
                <SystemFlow nodes={f.nodes} label={f.label} testId={`system-flow-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="glass-highlight" className="relative border-t border-white/10 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1680992046615-065f58bcb4d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByb29tJTIwYmx1ZSUyMGxpZ2h0JTIwZGFya3xlbnwwfHx8fDE3ODY2MTk3NDR8MA&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/85 to-[#050505]" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36">
          <Reveal>
            <div className="flex items-center gap-4 mb-10">
              <Glasses size={26} className="text-cyan" strokeWidth={1.5} />
              <p className="font-mono text-[11px] tracking-[0.35em] text-cyan">06 // WEARABLE PIONEER</p>
            </div>
          </Reveal>
          <MaskedLine className="font-unbounded font-semibold uppercase tracking-tight leading-[1.1] text-2xl sm:text-4xl lg:text-5xl max-w-4xl">
            Dozens of Google Glass applications.
          </MaskedLine>
          <MaskedLine delay={0.12} className="font-unbounded font-semibold uppercase tracking-tight leading-[1.1] text-2xl sm:text-4xl lg:text-5xl max-w-4xl text-outline">
            Built at the platform's original release.
          </MaskedLine>
          <Reveal delay={0.25}>
            <p className="mt-8 max-w-2xl text-sm md:text-base text-neutral-400 leading-relaxed">
              Long before today's wave of AI-powered smart glasses, I was an early developer for Google Glass — designing and building dozens of applications for one of the first wearable computing platforms. That experience now informs how I approach every new generation of emerging technology.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <Link
              to="/contact"
              data-testid="experience-cta-talk"
              className="group mt-10 inline-flex items-center gap-3 border border-cyan/50 px-8 py-4 font-mono text-xs tracking-[0.25em] text-cyan hover:bg-cyan hover:text-black transition-colors duration-300"
            >
              DISCUSS YOUR PROJECT
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
